import {
    Card,
    CardContent,
    CardHeader,
    Divider,
    ListItem,
    ListItemText,
    Switch,
    TextField,
    Tabs,
    Tab,
    Box
} from "@mui/material";
import { useEffect, useState } from "react";
import { PasswordConfirmationDialog } from "../dialogs/PasswordConfirmationDialog";
import { RestoreSettingsPasswordConfirmationDialog } from "../dialogs/RestoreSettingsPasswordConfirmationDialog";
import { CompanySetting, restoreCompanySettings, updateCompanySettings } from "../companySettingsAPI";
import { useAppDispatch } from "../../../../app/hooks";
import { showSuccess, showError } from "../../../notifications/notificationsSlice";
import { CardActionButtons } from "./CardActionButtons";
import { Localized } from "@fluent/react";

interface Props {
    companySettings: CompanySetting[];
    onUpdate: (updatedSettings: CompanySetting[]) => void;
}

const CODE_HAND_SIGNATURE = "PRINT_APPROVAL_HANDWRITTEN_SIGNATURE_COLUMN";
const CODE_EXTRA_APPROVAL = "PRINT_EXTRA_APPROVAL_SIGNATURE_LINES";
const CODE_EXTRA_ATTACH = "PRINT_EXTRA_ATTACHMENT_LINES";

export const CompanyPrintSettingsCard: React.FC<Props> = ({ companySettings, onUpdate }) => {
    const dispatch = useAppDispatch();

    const [isEditing, setIsEditing] = useState(false);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [restoreDialogOpen, setRestoreDialogOpen] = useState(false);
    const [saving, setSaving] = useState(false);
    const [serverError, setServerError] = useState("");

    const [handSignature, setHandSignature] = useState<CompanySetting | undefined>();
    const [extraApproval, setExtraApproval] = useState<CompanySetting | undefined>();
    const [extraAttach, setExtraAttach] = useState<CompanySetting | undefined>();

    useEffect(() => {
        setHandSignature(companySettings.find(s => s.code === CODE_HAND_SIGNATURE));
        setExtraApproval(companySettings.find(s => s.code === CODE_EXTRA_APPROVAL));
        setExtraAttach(companySettings.find(s => s.code === CODE_EXTRA_ATTACH));
    }, [companySettings]);

    const handleSave = () => setDialogOpen(true);
    const handleRestore = () => setRestoreDialogOpen(true);

    const handleConfirmSave = async (password: string) => {
        if (!password) return;

        setSaving(true);
        setServerError("");

        try {
            const updated: CompanySetting[] = [
                handSignature!,
                extraApproval!,
                extraAttach!
            ];

            const result = await dispatch(updateCompanySettings({ password, settings: updated }));

            if (updateCompanySettings.fulfilled.match(result)) {
                onUpdate(result.payload as CompanySetting[]);
                setDialogOpen(false);
                setIsEditing(false);
                dispatch(showSuccess("saved"));
            } else {
                setServerError("error");
            }
        }
        catch {
            dispatch(showError("error"));
        }
        finally {
            setSaving(false);
        }
    };

    const handleConfirmRestore = async (password: string) => {
        if (!password) return;

        setSaving(true);
        setServerError("");

        try {
            const result = await dispatch(restoreCompanySettings({
                password,
                settings: [CODE_HAND_SIGNATURE, CODE_EXTRA_APPROVAL, CODE_EXTRA_ATTACH]
            }));

            if (restoreCompanySettings.fulfilled.match(result)) {
                onUpdate(result.payload as CompanySetting[]);
                setRestoreDialogOpen(false);
                setIsEditing(false);
                dispatch(showSuccess("saved"));
            } else {
                setServerError("error");
            }
        }
        catch {
            dispatch(showError("error"));
        }
        finally {
            setSaving(false);
        }
    };

    const setNumberValue = (setting: CompanySetting | undefined, value: number) => {
        if (!setting) return;
        setting.value = String(Math.max(0, value));
        return { ...setting };
    };

    return (
        <Card sx={{ maxWidth: 500, borderRadius: 2, boxShadow: 3 }}>
            <CardHeader
                title={<Localized id="company-settings-print">
                    <span>_Wydruki</span>
                </Localized>}
                sx={{
                    textAlign: "center",
                    bgcolor: "primary.main",
                    color: "white",
                    py: 1
                }}
            />

            <CardContent>

                <Tabs value={0}>
                    <Tab label={<Localized id="general">
                        <span>_Ogólne</span>
                    </Localized>} />
                </Tabs>

                <Box mt={2}>

                    <ListItem>
                        <ListItemText primary={<Localized id="company-settings-print-hand-signature">
                            <span>_Kolumna podpisu odręcznego</span>
                        </Localized>} />
                        <Switch
                            checked={handSignature?.isActive || false}
                            disabled={!isEditing}
                            onChange={() =>
                                setHandSignature(prev => prev ? { ...prev, isActive: !prev.isActive } : prev)
                            }
                        />
                    </ListItem>

                    <ListItem>
                        <ListItemText primary={<Localized id="company-settings-print-extra-approval">
                            <span>_Dodatkowe linie podpisów</span>
                        </Localized>} />
                        <TextField
                            type="number"
                            size="small"
                            disabled={!isEditing}
                            value={extraApproval?.value ?? "0"}
                            onChange={(e) =>
                                setExtraApproval(setNumberValue(extraApproval, Number(e.target.value)))
                            }
                            sx={{ width: 100 }}
                        />
                    </ListItem>

                    <ListItem>
                        <ListItemText primary={<Localized id="company-settings-print-extra-attach">
                            <span>_Dodatkowe linie załączników</span>
                        </Localized>} />
                        <TextField
                            type="number"
                            size="small"
                            disabled={!isEditing}
                            value={extraAttach?.value ?? "0"}
                            onChange={(e) =>
                                setExtraAttach(setNumberValue(extraAttach, Number(e.target.value)))
                            }
                            sx={{ width: 100 }}
                        />
                    </ListItem>

                </Box>

                <Divider sx={{ mt: 2 }} />

                <CardActionButtons
                    isDefault={false}
                    isEditing={isEditing}
                    onRestore={handleRestore}
                    onSave={handleSave}
                    onCancel={() => setIsEditing(false)}
                    onEdit={() => setIsEditing(true)}
                />

            </CardContent>

            {dialogOpen &&
                <PasswordConfirmationDialog
                    saving={saving}
                    serverError={serverError}
                    onClose={() => setDialogOpen(false)}
                    onConfirm={handleConfirmSave}
                />
            }

            {restoreDialogOpen &&
                <RestoreSettingsPasswordConfirmationDialog
                    saving={saving}
                    serverError={serverError}
                    onClose={() => setRestoreDialogOpen(false)}
                    onConfirm={handleConfirmRestore}
                />
            }
        </Card>
    );
};